import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Play, ClipboardList, Calendar } from "lucide-react";
import { QuizViewer } from "@/components/QuizViewer";
import { useNavigate } from "react-router-dom";

interface QuizSet {
  id: string;
  title: string;
  total_questions: number;
  created_at: string;
}

const Quiz = () => {
  const { user, loading: authLoading } = useAuth();
  const [quizSets, setQuizSets] = useState<QuizSet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedQuiz, setSelectedQuiz] = useState<QuizSet | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      loadQuizSets();
    }
  }, [user]);

  const loadQuizSets = async () => {
    const { data, error } = await supabase
      .from("quiz_sets")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      toast({
        title: "Error",
        description: "Failed to load quiz sets",
        variant: "destructive",
      });
    } else {
      setQuizSets(data || []);
    }
    setIsLoading(false);
  };

  const handleDeleteQuiz = async (id: string) => {
    const { error } = await supabase.from("quiz_sets").delete().eq("id", id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to delete quiz",
        variant: "destructive",
      });
    } else {
      setQuizSets((prev) => prev.filter((q) => q.id !== id));
      toast({
        title: "Success",
        description: "Quiz deleted successfully",
      });
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <main className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8 pt-24">
        <h1 className="text-3xl font-bold mb-6">My Quizzes</h1>

        {selectedQuiz ? (
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl font-semibold mb-4">{selectedQuiz.title}</h2>
            <QuizViewer
              setId={selectedQuiz.id}
              title={selectedQuiz.title}
              onClose={() => setSelectedQuiz(null)}
            />
          </div>
        ) : quizSets.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <ClipboardList className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                You haven't created any quizzes yet.
              </p>
              <Button onClick={() => navigate("/")}>Create Your First Quiz</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {quizSets.map((quiz) => (
              <Card key={quiz.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg line-clamp-2">{quiz.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                    <ClipboardList className="w-4 h-4" />
                    <span>{quiz.total_questions} questions</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(quiz.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      className="flex-1"
                      onClick={() => setSelectedQuiz(quiz)}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Start Quiz
                    </Button>
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => handleDeleteQuiz(quiz.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};

export default Quiz;
